import {
  faBan,
  faBars,
  faCamera,
  faCopy,
  faGear,
  faPrint,
  faSignOut,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import copyToClipboard from 'copy-to-clipboard';
import React from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import {
  Alert,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  UncontrolledDropdown
} from 'reactstrap';

import { generateCameraLink, replaceCameraLink } from '../Firebase/http';
import { useGlobalState } from '../GlobalState/GlobalState';
import { router } from '../Routes/AppRoutes';
import { getCameraLink } from '../Utils/HelperFunctions/helperFunctions';
import { useIsMobileView } from '../Utils/HelperFunctions/Hooks';
import evidSymbol from '../Utils/img/evid-symbol.svg';
import { UserRole } from '../Utils/types';

import styles from './AppShell.module.scss';
import { AppButton } from './ui/AppButton';
import { AppDialog } from './ui/AppDialog';
import { BrandMark } from './ui/BrandMark';
import { IconButton } from './ui/IconButton';

// The page the router is on, for marking the current place in the top bar (OQ-181).
const useCurrentPath = () => {
  const [path, setPath] = React.useState<string>(() => router.state?.location.pathname ?? window.location.pathname);
  React.useEffect(() => router.subscribe?.((state) => setPath(state.location.pathname)), []);
  return path;
};

const initials = (name?: string) =>
  (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('') || '?';

const isAdminRole = (role?: UserRole) => role === UserRole.SUPERADMIN || role === UserRole.ADMIN;
// The org colour is free text from the settings; only a hex value goes into the style.
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const AppShell: React.FC<{ children: React.ReactNode }> = (props) => {
  const globalState = useGlobalState();
  const [navbarVisible, setNavbarVisible] = React.useState<boolean>(false);
  const [showGeneratedLinkModal, setShowGeneratedLinkModal] = React.useState<boolean>(false);
  const [isLoadingGeneratedLink, setIsLoadingGeneratedLink] = React.useState<boolean>(false);
  const [tempGeneratedLinkId, setTempGeneratedLinkId] = React.useState<string>('');
  const [permaGeneratedLinkId, setPermaGeneratedLinkId] = React.useState<string>('');
  const [permaLinkFid, setPermaLinkFid] = React.useState<string>('');
  const isMobileView = useIsMobileView();
  const { t } = useTranslation();
  const isAdmin = isAdminRole(globalState.user?.role);

  const orgLogo = globalState.organisation?.organisationLogo;
  // The organisation's own colour is shown only here, as the top bar's lower edge (decisions/0011).
  const orgColor = globalState.organisation?.organisationColor;

  const onClickGenerateLink = async () => {
    setIsLoadingGeneratedLink(true);
    try {
      const tempLinkData = await generateCameraLink(globalState.organisation?.docId || '');
      const permaLinkData = await generateCameraLink(globalState.organisation?.docId || '', true);

      setNavbarVisible(false);
      setTempGeneratedLinkId(getCameraLink(tempLinkData.fId));
      setPermaGeneratedLinkId(getCameraLink(permaLinkData.fId));
      setPermaLinkFid(permaLinkData.fId);
      setShowGeneratedLinkModal(true);
    } catch (error) {
      console.log('Error generating link:', error);
      toast.error(t('shell.appShell.somethingWentWrong'));
    } finally {
      setIsLoadingGeneratedLink(false);
    }
  };

  // OQ-115: revoke the organisation's permanent link and show its replacement.
  const onReplacePermanent = async () => {
    const replacement = await replaceCameraLink(permaLinkFid);
    const url = getCameraLink(replacement.fId);
    setPermaLinkFid(replacement.fId);
    setPermaGeneratedLinkId(url);
    return url;
  };

  return (
    <div className={styles.shellContainer}>
      <header
        className={styles.navbar}
        style={orgColor && HEX_COLOR.test(orgColor) ? { boxShadow: `inset 0 -3px 0 ${orgColor}` } : undefined}
      >
        <a href='/' onClick={navigateInApp('/')} className={styles.brand}>
          <img src={orgLogo ?? evidSymbol} alt={t('shell.brandAlt')} className={styles.brandLogo} />
          <span className='text-truncate'>{globalState.organisation?.displayName ?? 'EVID'}</span>
        </a>
        {isMobileView ? (
          <IconButton
            icon={faBars}
            label={t('shell.appShell.openMenu')}
            variant='inverse'
            className='ms-auto'
            onClick={() => setNavbarVisible(!navbarVisible)}
          />
        ) : (
          <>
            <PlaceLinks />
            <div className={styles.right}>
              {isAdmin && (
                <AppButton
                  variant='accent'
                  size='sm'
                  icon={faCamera}
                  loading={isLoadingGeneratedLink}
                  onClick={onClickGenerateLink}
                >
                  {t('shell.appShell.generateCameraLink')}
                </AppButton>
              )}
              <UncontrolledDropdown>
                <DropdownToggle
                  tag='button'
                  type='button'
                  className={styles.avatar}
                  aria-label={t('shell.appShell.accountMenu', { name: globalState.user?.displayName ?? '' })}
                >
                  {initials(globalState.user?.displayName)}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem header>{globalState.user?.displayName}</DropdownItem>
                  <DropdownItem tag='a' href='/settings' onClick={navigateInApp('/settings')}>
                    <FontAwesomeIcon icon={faGear} className='me-2' />
                    {t('shell.appShell.settings')}
                  </DropdownItem>
                  <DropdownItem divider />
                  <DropdownItem onClick={() => globalState.logout()}>
                    <FontAwesomeIcon icon={faSignOut} className='me-2' />
                    {t('shell.appShell.logout')}
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </>
        )}
      </header>

      <Offcanvas
        isOpen={navbarVisible}
        toggle={() => setNavbarVisible(false)}
        direction='end'
        className={styles.offcanvas}
      >
        <OffcanvasHeader
          close={
            <IconButton
              icon={faXmark}
              label={t('common.close')}
              variant='inverse'
              onClick={() => setNavbarVisible(false)}
            />
          }
          className={styles.offcanvasHeader}
        >
          <a
            href='/'
            onClick={navigateInApp('/', () => setNavbarVisible(false))}
            className='fw-bold text-light text-decoration-none d-flex gap-2 align-items-center'
          >
            <span className='d-flex flex-column gap-1'>
              <BrandMark />
              {globalState.user?.role !== UserRole.SUPERADMIN && (
                <span className='fw-normal fst-italic small d-inline-block text-truncate' style={{ maxWidth: '15rem' }}>
                  {globalState.organisation?.displayName || ''}
                </span>
              )}
            </span>
          </a>
        </OffcanvasHeader>
        <OffcanvasBody className='d-flex flex-column gap-1'>
          <PlaceLinks vertical onNavigate={() => setNavbarVisible(false)} />
          <hr className='border-light opacity-25' />
          {isAdmin && (
            <AppButton
              variant='accent'
              icon={faCamera}
              loading={isLoadingGeneratedLink}
              onClick={onClickGenerateLink}
              className='justify-content-start'
            >
              {t('shell.appShell.generateCameraLink')}
            </AppButton>
          )}
          <a
            href='/settings'
            onClick={navigateInApp('/settings', () => setNavbarVisible(false))}
            className={styles.placeLink}
          >
            <FontAwesomeIcon icon={faGear} className='me-2' />
            {t('shell.appShell.settings')}
          </a>
          <button
            type='button'
            className={`${styles.placeLink} ${styles.placeButton}`}
            onClick={() => globalState.logout()}
          >
            <FontAwesomeIcon icon={faSignOut} className='me-2' />
            {t('shell.appShell.logout')}
          </button>
        </OffcanvasBody>
      </Offcanvas>

      <GeneratedLinkModal
        isOpen={showGeneratedLinkModal}
        setIsOpen={setShowGeneratedLinkModal}
        tempLink={tempGeneratedLinkId}
        permaLink={permaGeneratedLinkId}
        onReplacePermanent={onReplacePermanent}
      />

      <div className={styles.mainContainer}>{props.children}</div>
    </div>
  );
};

// AppShell renders outside the RouterProvider (App.tsx), so it cannot use <Link>. Its links keep a real
// href for new-tab clicks and switch page through the data router on a plain click (OQ-107).
const navigateInApp = (path: string, beforeNavigate?: () => void) => (e: React.MouseEvent) => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  beforeNavigate?.();
  void router.navigate(path);
};

// The app's places: media items for everyone, Projects and Drawings for an ADMIN (OQ-181).
const PlaceLinks = ({ vertical, onNavigate }: { vertical?: boolean; onNavigate?: () => void }) => {
  const globalState = useGlobalState();
  const { t } = useTranslation();
  const path = useCurrentPath();
  const places = [
    { to: '/', label: t('shell.appShell.media'), current: path === '/' || path.startsWith('/media') },
    ...(isAdminRole(globalState.user?.role)
      ? [
          { to: '/projects', label: t('shell.appShell.projects'), current: path.startsWith('/projects') },
          { to: '/drawings', label: t('shell.appShell.drawings'), current: path.startsWith('/drawings') }
        ]
      : [])
  ];
  return (
    <nav aria-label={t('shell.appShell.places')} className={vertical ? 'd-flex flex-column gap-1' : styles.places}>
      {places.map((place) => (
        <a
          key={place.to}
          href={place.to}
          onClick={navigateInApp(place.to, onNavigate)}
          aria-current={place.current ? 'page' : undefined}
          className={styles.placeLink}
        >
          {place.label}
        </a>
      ))}
    </nav>
  );
};

type GeneratedLinkModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  tempLink: string;
  permaLink: string;
  // Revokes the permanent link and resolves to the new link's URL (OQ-115).
  onReplacePermanent?: () => Promise<string>;
};
export const GeneratedLinkModal = ({
  isOpen,
  setIsOpen,
  tempLink,
  permaLink: initialPermaLink,
  onReplacePermanent
}: GeneratedLinkModalProps) => {
  const tempQrRef = React.useRef<HTMLDivElement>(null);
  const permaQrRef = React.useRef<HTMLDivElement>(null);
  const [replacedPermaLink, setReplacedPermaLink] = React.useState<string>();
  const [confirmingReplace, setConfirmingReplace] = React.useState(false);
  const [replacing, setReplacing] = React.useState(false);
  const permaLink = replacedPermaLink ?? initialPermaLink;
  const tempLabelId = React.useId();
  const permaLabelId = React.useId();
  const { t } = useTranslation();

  React.useEffect(() => setReplacedPermaLink(undefined), [initialPermaLink]);
  // AppShell keeps the modal mounted, so a confirmation left open must not greet the next opening.
  React.useEffect(() => {
    if (!isOpen) setConfirmingReplace(false);
  }, [isOpen]);

  const replacePermanent = async () => {
    if (!onReplacePermanent) return;
    setReplacing(true);
    try {
      setReplacedPermaLink(await onReplacePermanent());
      setConfirmingReplace(false);
      toast.success(t('shell.linkModal.replaced'));
    } catch (error) {
      console.log('Error replacing link:', error);
      toast.error(t('shell.linkModal.replaceFailed'));
    } finally {
      setReplacing(false);
    }
  };

  const printTemp = useReactToPrint({ contentRef: tempQrRef });
  const printPerma = useReactToPrint({ contentRef: permaQrRef });

  return (
    <AppDialog isOpen={isOpen} onClose={() => setIsOpen(false)} title={t('shell.linkModal.title')}>
      <p>{t('shell.linkModal.validFor')}</p>
      <p>{t('shell.linkModal.afterExpiry')}</p>
      {/* each link with its own buttons, in a row that wraps inside the dialog (OQ-167) */}
      <div role='group' aria-labelledby={tempLabelId} className='mb-3'>
        <p className='mb-2'>
          <span id={tempLabelId}>{t('shell.linkModal.tempLink')}</span>{' '}
          <a href={tempLink} target='_blank' rel='noreferrer' style={{ wordBreak: 'break-word' }}>
            {tempLink}
          </a>
        </p>
        <div className='d-flex flex-wrap gap-1'>
          <AppButton
            size='sm'
            icon={faCopy}
            onClick={() => {
              copyToClipboard(tempLink);
              toast.success(t('shell.linkModal.urlCopied'));
            }}
          >
            {t('shell.linkModal.copyTemp')}
          </AppButton>
          <AppButton size='sm' icon={faPrint} onClick={printTemp}>
            {t('shell.linkModal.printTemp')}
            {/* this qr code is only visible on the print-out */}
            <span style={{ overflow: 'hidden', width: 0, height: 0, position: 'absolute' }}>
              <div ref={tempQrRef}>
                <QRCode
                  value={tempLink}
                  style={{ position: 'relative', left: 'calc(50% - 128px)', top: 'calc(50vh - 128px)' }}
                />
              </div>
            </span>
          </AppButton>
        </div>
      </div>
      <div role='group' aria-labelledby={permaLabelId} className='mb-3'>
        <p className='mb-2'>
          <span id={permaLabelId}>{t('shell.linkModal.permaLink')}</span>{' '}
          <a href={permaLink} target='_blank' rel='noreferrer' style={{ wordBreak: 'break-word' }}>
            {permaLink}
          </a>
        </p>
        <div className='d-flex flex-wrap gap-1'>
          <AppButton
            size='sm'
            icon={faCopy}
            onClick={() => {
              copyToClipboard(permaLink);
              toast.success(t('shell.linkModal.urlCopied'));
            }}
          >
            {t('shell.linkModal.copyPerma')}
          </AppButton>
          <AppButton size='sm' icon={faPrint} onClick={printPerma}>
            {t('shell.linkModal.printPerma')}
            {/* this qr code is only visible on the print-out */}
            <span style={{ overflow: 'hidden', width: 0, height: 0, position: 'absolute' }}>
              <div ref={permaQrRef}>
                <QRCode
                  value={permaLink}
                  style={{ position: 'relative', left: 'calc(50% - 128px)', top: 'calc(50vh - 128px)' }}
                />
              </div>
            </span>
          </AppButton>
          {onReplacePermanent && (
            <AppButton
              size='sm'
              variant='danger-ghost'
              icon={faBan}
              onClick={() => setConfirmingReplace(true)}
              disabled={confirmingReplace || replacing}
            >
              {t('shell.linkModal.revokePerma')}
            </AppButton>
          )}
        </div>
      </div>
      {confirmingReplace && (
        <Alert color='danger' className='mb-0'>
          <p>{t('shell.linkModal.replaceWarning')}</p>
          <div className='d-flex justify-content-end gap-1'>
            <AppButton size='sm' variant='ghost' onClick={() => setConfirmingReplace(false)} disabled={replacing}>
              {t('common.cancel')}
            </AppButton>
            <AppButton size='sm' variant='danger' onClick={replacePermanent} loading={replacing}>
              {t('shell.linkModal.revokeAndCreate')}
            </AppButton>
          </div>
        </Alert>
      )}
    </AppDialog>
  );
};
